<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { type MobileScreen, getMobileScreen } from '@/components/Mobile/mobile-screen';

type DetailTab = Extract<MobileScreen, 'conversation' | 'resume' | 'contact'>;

const TABS: { key: DetailTab; label: string }[] = [
  { key: 'conversation', label: 'Conversation' },
  { key: 'resume', label: 'Resume' },
  { key: 'contact', label: 'Contact' },
];

const route = useRoute();
const router = useRouter();

const activeTab = computed(() => getMobileScreen(route));

function routeParam(name: 'projectId' | 'conversationId'): string | undefined {
  const value = route.params[name];
  return Array.isArray(value) ? value[0] : value;
}

// Tab clicks only change the trailing path segment, so useJobsRouting treats it
// as a tab change and skips reloading the conversation (see its isTabChange guard).
function selectTab(tab: DetailTab): void {
  if (tab === activeTab.value) {
    return;
  }
  const projectId = routeParam('projectId');
  const conversationId = routeParam('conversationId');
  if (!projectId || !conversationId) {
    return;
  }
  router.push({ path: `/jobs/${projectId}/candidates/${conversationId}/${tab}` });
}
</script>

<template>
  <nav class="bg-shade-860 px-4 pb-2" data-test="mobile-detail-tabs">
    <div class="flex rounded-md bg-shade-840 p-0.5">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        type="button"
        class="flex flex-1 items-center justify-center rounded-md p-2.5 text-center text-xs"
        :class="
          activeTab === tab.key ? 'bg-tint-0 font-bold text-shade-900' : 'font-normal text-tint-0'
        "
        :aria-current="activeTab === tab.key ? 'page' : undefined"
        :data-test="`mobile-detail-tab-${tab.key}`"
        @click="selectTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>
  </nav>
</template>
